/* This component owns no global theme settings or newsletter storage keys. */
(() => {
  if (customElements.get('cvl-subscribe-offer')) return;
  class EmblemOffer extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.dialog = this.querySelector('dialog');
      this.launcher = this.querySelector('.cvl-offer__launcher');
      this.dock = this.querySelector('.cvl-offer__dock');
      this.cartOpenOnMobile = false;
      if (!this.dialog?.showModal) return;
      this.key = `cvl-email-offer:${window.Shopify?.theme?.id || 'local'}:${this.dataset.productId}:${this.dataset.code}`;
      const read = (storage, suffix) => { try { return window[storage].getItem(this.key + suffix) === '1'; } catch (_) { return false; } };
      const success = Boolean(this.querySelector('[data-offer-success]'));
      const subscribed = success || read('localStorage', ':subscribed') || read('localStorage', ':claimed');
      if (success) this.remember('localStorage', ':subscribed');
      this.querySelector('[data-offer-signup]').hidden = subscribed;
      this.querySelector('[data-offer-subscribed]').hidden = !subscribed;
      this.updateOfferPrice();
      this.launcher.addEventListener('click', () => this.open(), options);
      this.dialog.addEventListener('cancel', event => { event.preventDefault(); this.dismiss(); }, options);
      document.addEventListener('pointerdown', event => { if (!this.dock.contains(event.target)) this.expandSide(false); }, options);
      this.dock.addEventListener('keydown', event => { if (event.key === 'Escape') { this.expandSide(false); this.launcher.focus(); } }, options);
      this.querySelectorAll('[data-offer-close]').forEach(button => button.addEventListener('click', () => this.dismiss(), options));
      this.dialog.addEventListener('close', () => { this.showLauncher(); this.launcher.focus({ preventScroll: true, focusVisible: false }); }, options);
      this.dialog.addEventListener('click', event => {
        if (event.target !== this.dialog) return;
        const box = this.dialog.getBoundingClientRect();
        if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) this.dismiss();
      }, options);
      // Optional subscription keeps the native form/CAPTCHA flow; purchase is independent.
      this.querySelector('form').addEventListener('submit', () => this.remember('sessionStorage', ':shown'), options);
      if (success || this.querySelector('[data-offer-errors]')) { this.open(); return; }
      if (subscribed || read('sessionStorage', ':shown')) { this.showLauncher(); return; }
      // On mobile, the cart drawer has priority over this full-screen offer.
      this.cartObserver = new MutationObserver(() => this.syncMobileCartState());
      this.cartObserver.observe(document.documentElement, { subtree: true, attributes: true, attributeFilter: ['open', 'class'] });
      document.addEventListener('cart:change', () => this.syncMobileCartState(), options);
      this.startAutoTimer();
      this.syncMobileCartState();
      document.addEventListener('visibilitychange', () => { this.lastTick = performance.now(); }, options);
    }
    isMobileViewport() { return matchMedia('(max-width: 749px)').matches; }
    mobileCartIsOpen() {
      return this.isMobileViewport() && Boolean(document.querySelector('cart-drawer[open], cart-notification[open]'));
    }
    // Pauses the auto-open countdown while the mobile cart is open; desktop is never paused.
    syncMobileCartState() {
      if (this.mobileCartIsOpen()) {
        this.cartOpenOnMobile = true;
        clearInterval(this.timer);
        this.timer = null;
        if (this.dialog.open) this.dismiss();
        return;
      }
      if (this.cartOpenOnMobile) {
        this.cartOpenOnMobile = false;
        this.startAutoTimer();
      }
    }
    startAutoTimer() {
      if (this.shown) return;
      clearInterval(this.timer);
      this.remaining = 20000 + Math.random() * 10000;
      this.lastTick = performance.now();
      this.timer = setInterval(() => {
        const now = performance.now();
        if (!document.hidden) this.remaining -= now - this.lastTick;
        this.lastTick = now;
        if (this.remaining > 0 || document.hidden || this.mobileCartIsOpen()) return;
        if (document.querySelector('dialog[open], .cvl-modal-overlay, cart-drawer[open], navigation-drawer[open], search-drawer[open]') || /^(INPUT|TEXTAREA|SELECT)$/.test(document.activeElement?.tagName)) return;
        this.open();
      }, 250);
    }
    async updateOfferPrice() {
      const was = this.querySelector('[data-cvl-offer-was]');
      const now = this.querySelector('[data-cvl-offer-now]');
      const handle = this.dataset.productHandle;
      if (!was || !now || !handle) return;
      try {
        const response = await fetch(`/products/${encodeURIComponent(handle)}.js?country=US`, { credentials: 'same-origin' });
        if (!response.ok) return;
        const product = await response.json();
        const selected = product.variants?.find((variant) => String(variant.id) === String(this.dataset.variantId)) || product.variants?.[0];
        const rawPrice = Number(selected?.price);
        const original = rawPrice > 1000 ? rawPrice / 100 : rawPrice;
        const discount = Number(this.dataset.offerDiscount || 0);
        if (!Number.isFinite(original)) return;
        const format = (value) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(Math.max(0, value));
        was.textContent = format(original);
        now.textContent = format(original - discount);
      } catch (_) {
        // Keep the offer shell usable if the product endpoint is unavailable.
      }
    }
    expandSide(expanded) {
      this.dock.classList.toggle('is-expanded', expanded);
      this.launcher.setAttribute('aria-expanded', String(expanded));
      this.launcher.setAttribute('aria-label', expanded ? 'Open your exclusive offer' : 'Expand your surprise');
    }
    showLauncher() {
      this.expandSide(false);
      this.dock.hidden = false;
    }
    dismiss() {
      if (this.closing || !this.dialog.open) return;
      if (matchMedia('(prefers-reduced-motion: reduce)').matches) { this.dialog.close(); return; }
      this.closing = true;
      const animation = this.dialog.animate([{ opacity: 1, transform: 'scale(1)' }, { opacity: 0, transform: 'scale(.9)' }], { duration: 180, easing: 'ease-in' });
      animation.finished.catch(() => {}).then(() => { this.closing = false; if (this.dialog.open) this.dialog.close(); });
    }
    remember(storage, suffix) { try { window[storage].setItem(this.key + suffix, '1'); } catch (_) { /* Storage may be blocked. */ } }
    open() {
      if (this.dialog.open) return;
      this.shown = true;
      clearInterval(this.timer);
      this.timer = null;
      this.remember('sessionStorage', ':shown');
      this.expandSide(false);
      this.dock.hidden = true;
      this.dialog.showModal();
    }
    disconnectedCallback() {
      clearInterval(this.timer);
      this.timer = null;
      this.cartObserver?.disconnect();
      this.controller?.abort();
      this.controller = null;
      if (this.dialog?.open) this.dialog.close();
    }
  }
  customElements.define('cvl-subscribe-offer', EmblemOffer);
})();
